import * as Sentry from "@sentry/react";
import { Component, ReactNode } from "react";
import { TiptapErrorFallback } from "./TiptapErrorFallback";

type Props = {
  children: ReactNode;
  editorName?: string;
  documentId?: string;
  userId?: string;
  projectId?: string;
  onReset?: () => void;
};

type State = {
  hasError: boolean;
  error: Error | null;
};

export class TiptapErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const editorName = this.props.editorName || "TiptapEditor";

    Sentry.captureException(error, {
      tags: {
        errorType: "FatalEditorError",
        editorName,
      },
      extra: {
        componentStack: errorInfo.componentStack,
        documentId: this.props.documentId,
        userId: this.props.userId,
        projectId: this.props.projectId,
      },
      fingerprint: ["FatalEditorError", editorName],
    });

    Sentry.captureMessage(`TipTap Editor Crash: ${editorName}`, {
      level: "error",
      tags: { errorType: "FatalEditorError", editorName },
      extra: {
        documentId: this.props.documentId,
        userId: this.props.userId,
        projectId: this.props.projectId,
        originalError: error.message,
      },
    });
  }

  handleRetry = () => {
    this.props.onReset?.();
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return <TiptapErrorFallback onRetry={this.handleRetry} />;
    }
    return this.props.children;
  }
}

export default TiptapErrorBoundary;
